<template>
    <template v-if="activeThread">
        <div class="drawer-thread-meta">
            <span v-if="activeThread.created_by">
                {{ activeThread.created_by.name }}
            </span>
        </div>

        <template v-if="canContribute && hasPendingConflicts">
            <p class="conflict-hint" data-testid="drawer-conflict-hint">
                Accept is blocked while this thread is behind main.
            </p>
            <Button
                :label="`Resolve ${pendingConflicts.length} conflict${
                    pendingConflicts.length === 1 ? '' : 's'
                }`"
                icon="pi pi-arrow-right"
                severity="warn"
                outlined
                size="small"
                class="conflict-resolve-cta"
                data-testid="drawer-resolve-conflicts"
                @click="goToReconcile"
            />
        </template>

        <Button
            v-if="
                canContribute &&
                !hasPendingConflicts &&
                resolvedConflicts.length > 0
            "
            :label="`Review ${resolvedConflicts.length} resolved conflict${
                resolvedConflicts.length === 1 ? '' : 's'
            }`"
            icon="pi pi-check-circle"
            severity="secondary"
            outlined
            size="small"
            class="conflict-resolve-cta"
            data-testid="drawer-review-conflicts"
            @click="goToReconcile"
        />

        <div
            v-if="activeThread.status === 'open' && userChanges.length"
            class="drawer-user-changes"
            data-testid="drawer-user-changes"
        >
            <h4 class="drawer-user-changes-title">
                Changes made directly by users (not the agent)
            </h4>
            <div
                v-for="change in userChanges"
                :key="`${change.entity_type}:${change.entity_id}`"
                class="drawer-user-change-row"
                data-testid="drawer-user-change-row"
            >
                <span class="drawer-user-change-name">
                    {{ change.name || change.entity_id }}
                </span>
                <span class="drawer-user-change-meta">
                    {{ humanizeType(change.entity_type) }} &middot;
                    {{ change.diff_status }}
                </span>
            </div>
        </div>

        <div ref="threadBodyEl" class="drawer-thread-body">
            <ThreadTimeline
                :entries="activeEntries"
                :thread-id="threadId"
                :files-by-entry-id="filesByEntryId"
                :thread-open="canContribute"
                :responding-proposal-id="respondingProposalId"
                @confirm="(id) => handleProposalResponse(id, 'confirm')"
                @cancel="(id) => handleProposalResponse(id, 'cancel')"
                @open="(id, name) => handleOpen(id, name)"
                @download="(id, name) => handleDownload(id, name)"
            />
        </div>

        <div v-if="isWaitingForAgent" class="thread-agent-waiting">
            <ul v-if="agentProgressSteps.length" class="agent-steps">
                <li
                    v-for="(step, i) in agentProgressSteps"
                    :key="i"
                    class="agent-step"
                    :class="{
                        current: i === agentProgressSteps.length - 1,
                    }"
                >
                    <span class="step-label">
                        <i
                            :class="
                                i === agentProgressSteps.length - 1
                                    ? 'pi pi-spin pi-spinner'
                                    : 'pi pi-check'
                            "
                        />
                        <span>{{ step }}</span>
                    </span>
                    <span
                        v-if="
                            i === agentProgressSteps.length - 1 && elapsedLabel
                        "
                        class="agent-timer"
                        >{{ elapsedLabel }}</span
                    >
                </li>
            </ul>
            <div v-else class="agent-step current">
                <span class="step-label">
                    <i class="pi pi-spin pi-spinner" />
                    <span>Gotoko is replying…</span>
                </span>
                <span v-if="elapsedLabel" class="agent-timer">{{
                    elapsedLabel
                }}</span>
            </div>
        </div>

        <div
            v-if="unsentFiles.length || uploadingFileName"
            class="thread-attachments"
        >
            <div
                v-for="file in unsentFiles"
                :key="file.data.file_id"
                class="thread-attachment"
            >
                <button
                    type="button"
                    class="thread-attachment-open"
                    :title="`Open ${file.data.filename}`"
                    @click="handleOpen(file.data.file_id, file.data.filename)"
                >
                    <i class="pi pi-file" />
                    <span class="thread-attachment-name">{{
                        file.data.filename
                    }}</span>
                    <span
                        v-if="humanSize(file.data.size)"
                        class="thread-attachment-size"
                        >{{ humanSize(file.data.size) }}</span
                    >
                    <i class="pi pi-external-link" />
                </button>
                <button
                    type="button"
                    class="thread-attachment-download"
                    :title="`Download ${file.data.filename}`"
                    aria-label="Download file"
                    @click="
                        handleDownload(file.data.file_id, file.data.filename)
                    "
                >
                    <i class="pi pi-download" />
                </button>
                <button
                    v-if="canRemove(file)"
                    type="button"
                    class="thread-attachment-remove"
                    :title="`Remove ${file.data.filename}`"
                    aria-label="Remove file"
                    @click="handleRemove(file.data.file_id)"
                >
                    <i class="pi pi-times" />
                </button>
            </div>

            <div
                v-if="uploadingFileName"
                class="thread-attachment thread-attachment-uploading"
                aria-live="polite"
            >
                <i class="pi pi-spin pi-spinner" />
                <span class="thread-attachment-name"
                    >Uploading {{ uploadingFileName }}…</span
                >
            </div>
        </div>

        <ThreadInput
            v-if="canContribute"
            @submit="handleContribute"
            @typing="handleTyping"
            @attach="handleAttach"
            @attach-link="handleAttachLink"
        />
    </template>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import { computed, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import ThreadInput from "@/components/Thread/ThreadInput.vue";
import ThreadTimeline from "@/components/Thread/ThreadTimeline.vue";
import { deriveThreadFiles } from "@/components/Thread/thread-attachments";
import { humanSize } from "@/components/Thread/thread-content";
import { useEditManager } from "@/composables/edit-manager";
import { useAutoScrollToBottom } from "@/composables/useAutoScrollToBottom";
import { useThreadsStore } from "@/stores/threads-store";
import { useUserManager } from "@/stores/user-store";

const props = defineProps<{
    threadId: string;
}>();

const store = useThreadsStore();
const router = useRouter();
const { user } = useUserManager();
const { isViewOnly } = useEditManager();

const {
    activeThread,
    activeEntries,
    hasPendingConflicts,
    pendingConflicts,
    resolvedConflicts,
    userChanges,
    isWaitingForAgent,
    agentProgressSteps,
    agentRunStartedAt,
    respondingProposalId,
    attachedFiles,
    uploadingFileName,
} = storeToRefs(store);

// Writing to an open thread — a message, a file, a proposal answer, a
// conflict decision — is refused a viewer, who reads the conversation only.
const canContribute = computed(
    () => activeThread.value?.status === "open" && !isViewOnly.value
);

const threadBodyEl = ref<HTMLElement | null>(null);
useAutoScrollToBottom(threadBodyEl, activeEntries);

// The split between sent files (in message bubbles) and this user's unsent
// uploads (the composer strip) lives in thread-attachments.ts — the subtlest
// logic here, and where the per-user scoping of the strip is pinned.
const derivedFiles = computed(() =>
    deriveThreadFiles(
        activeEntries.value,
        attachedFiles.value,
        user.value?.identity
    )
);

// Committed files, grouped under the message they were sent with.
const filesByEntryId = computed(() => derivedFiles.value.filesByEntryId);

// Files optimistically cleared from the strip the moment a message is sent, so
// they don't linger through the contribute round-trip + refetch. Once that lands
// the derived rule above is authoritative again and this is cleared.
const justSentFileIds = ref<Set<string>>(new Set());

// My own not-yet-sent uploads — they wait in my composer strip. The per-user
// scoping is in deriveThreadFiles; here we only also drop the ones this tab has
// just sent, so they don't blink during the round trip.
const unsentFiles = computed(() =>
    attachedFiles.value.filter(
        (f) =>
            derivedFiles.value.unsentFileIds.has(f.data.file_id) &&
            !justSentFileIds.value.has(f.data.file_id)
    )
);

// Live elapsed timer for the running agent turn. `now` ticks every second only
// while the agent is working, so the label counts up without a permanent interval.
const now = ref(Date.now());
let elapsedTimer: ReturnType<typeof setInterval> | undefined;
watch(
    isWaitingForAgent,
    (waiting) => {
        if (waiting) {
            now.value = Date.now();
            elapsedTimer ??= setInterval(() => (now.value = Date.now()), 1000);
        } else if (elapsedTimer !== undefined) {
            clearInterval(elapsedTimer);
            elapsedTimer = undefined;
        }
    },
    { immediate: true }
);
onUnmounted(() => {
    if (elapsedTimer !== undefined) clearInterval(elapsedTimer);
});
const elapsedLabel = computed(() => {
    if (agentRunStartedAt.value === null) return "";
    const totalSeconds = Math.max(
        0,
        Math.floor((now.value - agentRunStartedAt.value) / 1000)
    );
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
});

const TYPE_LABELS: Record<string, string> = {
    business_entity: "domain object",
};
const humanizeType = (entityType: string): string =>
    TYPE_LABELS[entityType] ?? entityType.replace(/_/g, " ");

const goToReconcile = () => {
    router.push({ name: "reconcile", query: { draft: props.threadId } });
};

const handleContribute = async (content: string) => {
    // Clear the composer's attachments immediately; the message they ride with
    // only lands after the contribute round-trip + refetch.
    const sentNow = unsentFiles.value.map((f) => f.data.file_id);
    if (sentNow.length) {
        justSentFileIds.value = new Set([...justSentFileIds.value, ...sentNow]);
    }
    await store.contribute(props.threadId, content);
    // The refetch has landed: if it succeeded the files now match their message
    // (derived rule), if it failed they return to the strip. Either way, hand
    // authority back to the derived rule.
    if (sentNow.length) justSentFileIds.value = new Set();
};

const handleAttach = async (file: File) => {
    await store.uploadThreadFile(props.threadId, file);
};

const handleAttachLink = async (url: string) => {
    await store.attachThreadLink(props.threadId, url);
};

const handleOpen = (fileId: string, filename: string) => {
    void store.openThreadFile(props.threadId, fileId, filename);
};

const handleDownload = (fileId: string, filename: string) => {
    void store.downloadThreadFile(props.threadId, fileId, filename);
};

// Only the user who uploaded a file may remove it — the server enforces this
// too; the affordance just mirrors that so others don't see a button that 403s.
const canRemove = (file: { data: Record<string, string> }): boolean =>
    !!user.value?.identity && file.data.uploaded_by_id === user.value.identity;

const handleRemove = (fileId: string) => {
    void store.deleteThreadFile(props.threadId, fileId);
};

const handleProposalResponse = (
    entryId: string,
    response: "confirm" | "cancel"
) => {
    void store.respondToProposal(props.threadId, entryId, response);
};

const handleTyping = () => {
    void store.notifyTyping(props.threadId);
};
</script>

<style scoped>
/* Attachments strip above the composer: one chip per uploaded file, click to
   download. Square corners to match the app. */
.thread-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    padding: 0.4rem 0.65rem;
    border-top: 1px solid var(--p-surface-200);
}
/* The chip is a wrapper holding a download button and, for the uploader, a
   remove button. The border lives on the wrapper; the inner buttons are
   borderless and square. */
.thread-attachment {
    display: inline-flex;
    align-items: stretch;
    max-width: 100%;
    font-size: 0.78rem;
    border: 1px solid var(--p-surface-300);
    background: var(--p-surface-0);
    color: var(--p-text-color);
}
.thread-attachment-open {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
    padding: 0.2rem 0.5rem;
    font: inherit;
    cursor: pointer;
    border: none;
    background: transparent;
    color: inherit;
}
.thread-attachment-open:hover {
    background: var(--p-surface-50);
}
.thread-attachment-download {
    display: inline-flex;
    align-items: center;
    padding: 0 0.4rem;
    font: inherit;
    cursor: pointer;
    border: none;
    border-left: 1px solid var(--p-surface-200);
    background: transparent;
    color: var(--p-text-muted-color);
}
.thread-attachment-download:hover {
    background: var(--p-surface-50);
    color: var(--p-primary-color);
}
.thread-attachment-remove {
    display: inline-flex;
    align-items: center;
    padding: 0 0.4rem;
    font: inherit;
    cursor: pointer;
    border: none;
    border-left: 1px solid var(--p-surface-200);
    background: transparent;
    color: var(--p-text-muted-color);
}
.thread-attachment-remove:hover {
    background: var(--p-surface-50);
    color: var(--p-red-500, #ef4444);
}
/* In-progress upload: a non-interactive chip with a spinner. */
.thread-attachment-uploading {
    align-items: center;
    gap: 0.35rem;
    padding: 0.2rem 0.5rem;
    color: var(--p-text-muted-color);
}
.thread-attachment-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 12rem;
}
.thread-attachment-size {
    color: var(--p-text-muted-color);
    font-variant-numeric: tabular-nums;
}

.drawer-thread-meta {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
    margin-bottom: 0.75rem;
}

.conflict-hint {
    margin: 0 0 0.5rem;
    font-size: 0.82rem;
    color: var(--p-text-secondary-color);
}

.conflict-resolve-cta {
    margin-bottom: 0.75rem;
}

.drawer-user-changes {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin: 0.25rem 0 0.5rem;
}
.drawer-user-changes-title {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--p-text-secondary-color);
}
.drawer-user-change-row {
    display: flex;
    flex-direction: column;
    min-width: 0;
    border: 1px solid var(--p-surface-200);
    padding: 0.5rem 0.65rem;
}
.drawer-user-change-name {
    font-weight: 600;
    font-size: 0.88rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.drawer-user-change-meta {
    font-size: 0.72rem;
    color: var(--p-text-muted-color);
    font-family: monospace;
}

.drawer-thread-body {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 0.5rem;
}

.thread-agent-waiting {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    color: var(--p-text-secondary-color);
    border-top: 1px solid var(--p-surface-200);
}

.thread-agent-waiting .agent-steps {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

/* Two columns: the step label, and the timer aligned to the right on its row. */
.thread-agent-waiting .agent-step {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
    gap: 0.75rem;
}

.thread-agent-waiting .step-label {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    min-width: 0;
}

.thread-agent-waiting .step-label .pi {
    flex: none;
    align-self: center;
}

/* Completed steps read as done history; the current one stays emphasised. */
.thread-agent-waiting .agent-step:not(.current) {
    opacity: 0.6;
}

.thread-agent-waiting .agent-step .pi-check {
    color: var(--p-primary-color);
}

.thread-agent-waiting .agent-timer {
    font-variant-numeric: tabular-nums;
    opacity: 0.7;
    white-space: nowrap;
}
</style>
